import { fabric } from "fabric";

/**
 * Background image setup, brightness/contrast filters, zoom/pan wiring and the
 * render loop that keeps label/point sizes in sync. Mixed into BboxCanvas.prototype.
 */
export default {
    /** Canvas-space font size so labels keep a roughly constant on-screen size when zoomed. */
    labelFontSize() {
        const zoom = Math.max(this.canvas.getZoom(), 0.01);
        return Math.max(1, this.canvasWidth / 35 / zoom);
    },

    /** Canvas-space stroke so box borders stay ~boxBorder screen pixels when zoomed. */
    annotationStrokeWidth() {
        const zoom = Math.max(this.canvas.getZoom(), 0.01);
        return Math.max(0.25, this.boxBorder / zoom);
    },

    /** Horizontal label-chip padding in canvas space (scales with zoom). */
    labelPadXForZoom() {
        const zoom = Math.max(this.canvas.getZoom(), 0.01);
        return Math.max(1, this.labelPadX / zoom);
    },

    updateLabelsOnZoom() {
        let this2 = this;
        const fs = this2.labelFontSize();
        const stroke = this2.annotationStrokeWidth();
        const zoom = Math.max(this2.canvas.getZoom(), 0.01);
        this.canvas.getObjects().forEach((obj) => {
            try {
                if (obj.annotation_id && obj.strokeWidth > 0 && (obj.type === 'rect' || obj.type === 'polygon')) {
                    obj.set({ strokeWidth: stroke });
                    if (obj.shadow && typeof obj.shadow.blur === 'number') {
                        obj.shadow.blur = 6 / zoom;
                    }
                }
                if(obj.text && typeof obj.text !== "string") {
                    obj.text.set({ fontSize: fs, lineHeight: this2.labelLineHeight });
                    if (obj.text.shadow && typeof obj.text.shadow.blur === 'number') {
                        obj.text.shadow.blur = 3 / zoom;
                    }
                    if (obj.text.type === 'textbox') {
                        this2._layoutBoxLabelText(obj.text);
                    }
                    obj.text.set({
                        top: obj.top - obj.text.calcTextHeight() - 2,
                        left: obj.left,
                    });
                }
            } catch(e) {
                // obj.text may not be a renderable Fabric text in some states; ignore.
            }
        })
    },

    setPointValues() {
        let zoom = this.canvas.getZoom() / 2;
        this.point_radius = Math.max(3, this.canvasWidth / 100 / zoom);
        this.inner_point_radius = Math.max(1, this.canvasWidth / 300 / zoom);
    },

    _updateBackgroundImageEvented() {
        if (!this.imgInstance || !this.canvas) {
            return;
        }
        const active = this.canvas.getActiveObject();
        const annotationActive = !!(active && active.annotation_id && !active.text_box);
        const allowImagePan = this._spacePanKey || !annotationActive;
        this.imgInstance.evented = allowImagePan;
        this.imgInstance.hoverCursor = allowImagePan ? 'move' : 'default';
    },

    _beginMiddlePan(opt) {
        const canvas = this.canvas;
        if (this.read_only || !opt || !opt.e) {
            return false;
        }
        // Fabric: 1=left, 2=middle, 3=right
        if (opt.button !== 2) {
            return false;
        }
        opt.e.preventDefault();
        opt.e.stopPropagation();
        canvas.isDragging = true;
        canvas._middlePanning = true;
        canvas.selection = false;
        canvas.lastPosX = opt.e.clientX;
        canvas.lastPosY = opt.e.clientY;
        const active = canvas.getActiveObject();
        if (active) {
            canvas._middlePanLocked = active;
            canvas._middlePanLockMovementX = active.lockMovementX;
            canvas._middlePanLockMovementY = active.lockMovementY;
            active.lockMovementX = true;
            active.lockMovementY = true;
        }
        canvas.setCursor('grab');
        return true;
    },

    _moveMiddlePan(opt) {
        const canvas = this.canvas;
        if (!canvas._middlePanning || !canvas.isDragging || !opt || !opt.e) {
            return false;
        }
        const e = opt.e;
        const vpt = canvas.viewportTransform;
        vpt[4] += e.clientX - canvas.lastPosX;
        vpt[5] += e.clientY - canvas.lastPosY;
        this.requestRenderAll();
        canvas.lastPosX = e.clientX;
        canvas.lastPosY = e.clientY;
        window.dispatchEvent(new CustomEvent(this.canvasId + "_dragging", {
            detail: {
                view_port_transform: canvas.viewportTransform,
            }
        }));
        return true;
    },

    _endMiddlePan() {
        const canvas = this.canvas;
        if (!canvas._middlePanning) {
            return false;
        }
        canvas.setViewportTransform(canvas.viewportTransform);
        canvas.isDragging = false;
        canvas._middlePanning = false;
        canvas.selection = true;
        if (canvas._middlePanLocked) {
            canvas._middlePanLocked.lockMovementX = canvas._middlePanLockMovementX;
            canvas._middlePanLocked.lockMovementY = canvas._middlePanLockMovementY;
            canvas._middlePanLocked = null;
        }
        canvas.setCursor('default');
        return true;
    },

    setCanvasEvents() {
        let this2 = this;
        let canvas = this.canvas;
        if (!this._spacePanListenersAdded) {
            this._spacePanListenersAdded = true;
            this._onSpacePanKeyDown = (e) => {
                if (e.code === 'Space' && !e.repeat) {
                    this2._spacePanKey = true;
                    this2._updateBackgroundImageEvented();
                }
            };
            this._onSpacePanKeyUp = (e) => {
                if (e.code === 'Space') {
                    this2._spacePanKey = false;
                    this2._updateBackgroundImageEvented();
                }
            };
            window.addEventListener('keydown', this._onSpacePanKeyDown);
            window.addEventListener('keyup', this._onSpacePanKeyUp);
        }
        if (!this._middlePanListenersAdded) {
            this._middlePanListenersAdded = true;
            // Prevent browser auto-scroll on middle click over the canvas.
            const preventAuxClick = (e) => {
                if (e.button === 1) {
                    e.preventDefault();
                }
            };
            canvas.upperCanvasEl.addEventListener('mousedown', preventAuxClick);
            canvas.upperCanvasEl.addEventListener('auxclick', preventAuxClick);
            canvas.on('mouse:down', function (opt) {
                this2._beginMiddlePan(opt);
            });
            canvas.on('mouse:move', function (opt) {
                this2._moveMiddlePan(opt);
            });
            canvas.on('mouse:up', function () {
                this2._endMiddlePan();
            });
        }
        if(!this.read_only) {
            canvas.on('mouse:wheel', function (opt) {
                var delta = opt.e.deltaY;
                if(delta<0) {
                  delta = -100
                }
                else {
                  delta = 100;
                }
                let zoom = canvas.getZoom();
                zoom *= 0.999 ** delta;

                if (zoom > 20) zoom = 20;
                if (zoom < 1) zoom = 1;

                window.dispatchEvent(new CustomEvent(this2.canvasId + "_zooming", {
                        detail: {
                            zoom: zoom,
                        }
                    }));

                if(zoom === 1) {
                    this2.resetZoom();
                }
                else {
                    canvas.zoomToPoint({x: opt.e.offsetX, y: opt.e.offsetY}, zoom);
                    opt.e.preventDefault();
                    opt.e.stopPropagation();
                    this2.requestRenderAll();
                    this2.updateLabelsOnZoom();
                }
            });
        }
    },

    requestRenderAll() {
        let objects = this.canvas.getObjects();
        objects.sort((a, b) => (a.width * a.height < b.width * b.height) ? 1 : -1);

        this.setPointValues();

        for(let i=0; i<objects.length; i++) {
            if (objects[i].annotation) {
                this.canvas.bringToFront(objects[i])
            }
            if (objects[i].is_inner_point) {
                objects[i].set('radius', this.inner_point_radius)
                objects[i].set('left', objects[i].x1 - this.inner_point_radius)
                objects[i].set('top', objects[i].y1 - this.inner_point_radius)
            }
            if (objects[i].is_point) {
                objects[i].set('radius', this.point_radius)
                objects[i].set('left', objects[i].x1 - this.point_radius)
                objects[i].set('top', objects[i].y1 - this.point_radius)
            }
        }
        this.canvas.requestRenderAll();
    },

    setImage(image) {
        this.canvasWidth = this.originalCanvasWidth

        let naturalWidth = image.width;
        let naturalHeight = image.height;

        let scaleX = 0
        let scaleY = 0
        let scaledHeight = 0
        let scaledWidth = 0

        // Default (landscape mode)
        scaledWidth = this.canvasWidth
        scaleX = scaledWidth / naturalWidth
        scaledHeight = scaleX * naturalHeight
        scaleY = scaledHeight / naturalHeight
        this.scale = scaleX

        let maxHeight = window.innerHeight * 0.7;
        if(this.maxHeight) {
            maxHeight = this.maxHeight;
        }

        // Portrait mode
        if(scaledHeight > maxHeight || naturalHeight > naturalWidth) {
            scaledHeight = maxHeight;
            scaleY = scaledHeight / naturalHeight
            scaledWidth = scaleY * naturalWidth
            scaleX = scaledWidth / naturalWidth
            this.scale = scaleY
        }

        // Keep display aspect ratio equal to the source image. A previous
        // vertical stretch for ultra-wide images (aspect > 8) scaled the
        // fabric image without updating canvasHeight, so annotations no
        // longer lined up with the pixels (annotate + grid preview).
        this.canvasWidth = scaledWidth;
        this.canvasHeight = scaledHeight;
        this.bboxScaleX = scaledWidth / this.modelSize;
        this.bboxScaleY = this.bboxScaleX;

        document.querySelector('#' + this.canvasId).setAttribute('height', scaledHeight + 'px')
        document.querySelector('#' + this.canvasId).setAttribute('width', scaledWidth + 'px')

        let canvas = this.canvas;
        // setWidth/setHeight size the backing store with retina scaling applied.
        // Do NOT manually overwrite the canvas element width/height afterwards —
        // that desyncs Fabric's getPointer() from the controls' oCoords on HiDPI
        // displays and breaks control hover/scale/click hit-testing.
        this.canvas.setHeight(scaledHeight)
        this.canvas.setWidth(scaledWidth)
        this.canvas.scale = this.scale;

        image.set('scaleX', scaleX).set('scaleY', scaleY).set('selectable', false);

        let this2 = this;

        image.on('mousedown', function (opt) {

            if(opt.button === 3 || this2.alt_pressed) {
                let mouseDownX = opt.e.offsetX / this2.canvasWidth;
                let mouseDownY = opt.e.offsetY / this2.canvasHeight;

                window.dispatchEvent(new CustomEvent(this2.canvasId + "right_mouse_pressed", {
                    detail: {
                        x: mouseDownX,
                        y: mouseDownY
                    }
                }));
            }

            if (this2._beginMiddlePan(opt)) {
                return;
            }

            if (this2.placementMode && opt.button === 1) {
                canvas.selection = false;
                return;
            }

            const active = canvas.getActiveObject();
            if (active && active.annotation_id && !active.text_box) {
                canvas.isDragging = false;
                return;
            }

            var evt = opt.e;

            if(!canvas.readOnly) {
                canvas.isDragging = true;
            }
            canvas.selection = false;
            canvas.lastPosX = evt.clientX;
            canvas.lastPosY = evt.clientY;
            window.dispatchEvent(new CustomEvent(this2.canvasId + "canvas_selected", {
                detail: {
                    id: this2.canvasId,
                }
            }));

        });
        image.on('mousemove', function (opt) {
            if (this2.placementMode) {
                return;
            }
            // Middle-button pan is handled by canvas-level listeners.
            if (canvas._middlePanning) {
                return;
            }
            if (canvas.isDragging) {
                var e = opt.e;
                var vpt = canvas.viewportTransform;
                vpt[4] += e.clientX - canvas.lastPosX;
                vpt[5] += e.clientY - canvas.lastPosY;
                this2.requestRenderAll();
                canvas.lastPosX = e.clientX;
                canvas.lastPosY = e.clientY;

                window.dispatchEvent(new CustomEvent(this2.canvasId + "_dragging", {
                    detail: {
                        view_port_transform: canvas.viewportTransform,
                    }
                }));

            }
        });
        image.on('mouseup', function () {
            if (this2._endMiddlePan()) {
                return;
            }
            // on mouse up we want to recalculate new interaction
            // for all objects, so we call setViewportTransform
            canvas.setViewportTransform(canvas.viewportTransform);
            canvas.isDragging = false;
            canvas.selection = true;
        });

        image.on('mouseout', function() {
          if (canvas._middlePanning) {
              return;
          }
          canvas.isDragging = false;
        });

        this.addImageWithFilters(image, naturalWidth);
        this.setCanvasEvents();
        this.canvas.calcOffset();
        this._updateBackgroundImageEvented();

        this.draw_annotations = true;
    },

    setModelPadding(naturalHeight, naturalWidth) {
        let r = Math.min(this.canvasWidth / naturalHeight, this.canvasWidth / naturalWidth)
        let unpad_w = Math.round(naturalWidth * r)
        let unpad_h = Math.round(naturalHeight * r)
        let dw = this.canvasWidth - unpad_w
        let dh = this.canvasWidth - unpad_h
        dw = (dw % 32) / 2
        dh = (dh % 32) / 2
        this.dh = dh
        this.dw = dw
    },

    setImageById(imageId) {

        let imgElement = document.getElementById(imageId);
        let naturalWidth = imgElement.naturalWidth;

        let imgInstance = new fabric.Image(imgElement, {
            left: 0,
            top: 0,
            opacity: 1,
            selectable: false,
        });

        this.addImageWithFilters(imgInstance, naturalWidth)
    },

    initFilters(imgInstance) {
        this.filtersInitialized = true;

        let f = fabric.Image.filters;

        imgInstance.filters.push(new f.Brightness({brightness: 0.01}));
        imgInstance.filters.push(new f.Contrast({contrast: 0.01}));
        imgInstance.applyFilters();
    },

    updateContrast(value) {
        let imgInstance = this.imgInstance;
        let naturalWidth = this.naturalWidth
        if (this.filterError) {
            return;
        }
        try {
            if (!this.filtersInitialized) {
                this.initFilters(imgInstance, naturalWidth)
            }
            imgInstance.filters[1]['contrast'] = parseFloat(value);
            imgInstance.applyFilters();
        } catch (error) {
            console.log('filterError', error);
            this.filterError = true;
            imgInstance.filters = [];
            imgInstance.applyFilters();
        }
        this.requestRenderAll();
    },

    updateBrightness(value) {
        let imgInstance = this.imgInstance;
        let naturalWidth = this.naturalWidth

        if (this.filterError) {
            return;
        }
        try {
            if (!this.filtersInitialized) {
                this.initFilters(imgInstance, naturalWidth)
            }
            imgInstance.filters[0]['brightness'] = parseFloat(value);
            imgInstance.applyFilters();
        } catch (error) {
            console.log('filterError', error);
            this.filterError = true;
            imgInstance.filters = [];
            imgInstance.applyFilters();
        }
        this.requestRenderAll();
    },

    addImageWithFilters(imgInstance, naturalWidth) {
        this.canvas.add(imgInstance);
        this.imgInstance = imgInstance;
        this.naturalWidth = naturalWidth;
    },

    resetZoom() {
        // reset zoom
        this.canvas.zoomToPoint(new fabric.Point(this.canvas.width / 2, this.canvas.height / 2), 1.0);

        // reset movement
        let moveLeft = 0
        let moveTop = 0

        var vpt = this.canvas.viewportTransform;
        vpt[4] = moveLeft;
        vpt[5] = moveTop;

        this.updateLabelsOnZoom();

        this.canvas.requestRenderAll();
    },
};
